import { useCallback } from 'react';
import { useAtom } from 'jotai';
import {
    setupIntentPopupOpenAtom,
    setupIntentPopupCustomerIdAtom,
    setupIntentPopupStripeAccountAtom,
    setupIntentPopupPaymentDataAtom,
    setupIntentPopupInvoicePaymentIdAtom,
} from '../states/setup-intent-popup-atoms';

/**
 * Custom hook for managing setup intent popup state and logic
 * @returns Object containing popup state and control functions
 */
export const useSetupIntentPopup = () => {
    const [isSetupIntentPopupOpen, setIsSetupIntentPopupOpen] = useAtom(
        setupIntentPopupOpenAtom
    );
    const [currentCustomerId, setCurrentCustomerId] = useAtom(
        setupIntentPopupCustomerIdAtom
    );
    const [stripeAccount, setStripeAccount] = useAtom(
        setupIntentPopupStripeAccountAtom
    );
    const [paymentData, setPaymentData] = useAtom(
        setupIntentPopupPaymentDataAtom
    );
    const [invoicePaymentId, setInvoicePaymentId] = useAtom(
        setupIntentPopupInvoicePaymentIdAtom
    );

    const openPopup = useCallback(
        (
            customerId: number,
            stripeAccountParam: string | null,
            paymentDataParam: { amount: number; currency: string },
            invoicePaymentIdParam?: string | null
        ) => {
            setCurrentCustomerId(customerId);
            setIsSetupIntentPopupOpen(true);
            setStripeAccount(stripeAccountParam);
            setPaymentData(paymentDataParam);
            setInvoicePaymentId(invoicePaymentIdParam || null);
        },
        [
            setCurrentCustomerId,
            setIsSetupIntentPopupOpen,
            setStripeAccount,
            setPaymentData,
            setInvoicePaymentId,
        ]
    );

    const closePopup = useCallback(() => {
        setIsSetupIntentPopupOpen(false);
        setCurrentCustomerId(0);
        setStripeAccount(null);
        setPaymentData(null);
        setInvoicePaymentId(null);
    }, [
        setIsSetupIntentPopupOpen,
        setCurrentCustomerId,
        setStripeAccount,
        setPaymentData,
        setInvoicePaymentId,
    ]);

    return {
        isSetupIntentPopupOpen,
        openPopup,
        closePopup,
        currentCustomerId,
        stripeAccount,
        paymentData,
        invoicePaymentId,
    };
};
